// Sugalarm — Cycle 124: the IOB-card detail block, restructured into
// ring-matched insulin section(s) ABOVE a distinct Carbs section so insulin
// reads first and food is visually separated.
//
//   Insulin section (per active bolus, newest-first, ≤2 to match the rings):
//     ┌ tinted header band ─────────────┐   (tint = the dose's ring color)
//     │ ● First bolus   /   ● Correction │
//     └─────────────────────────────────┘
//       {units}u · at H:MM AM/PM            (units in the strongest ink)
//   Carbs section (when a food/carbs context exists, cycle-119 guarantee):
//     ┌ tinted header (distinct --accent) ┐
//     │ ◆ Carbs                           │
//     └───────────────────────────────────┘
//       <glyph> <food name> · ~Cg · at TIME
//
// Two layouts off the `variant` prop: mobile = vertical stack (header band
// then value line); kiosk/web = single-line compact rows (tinted label pill
// left, value right-aligned). All color comes from the single token seam —
// MEAL_BOLUS_COLOR / CORRECTION_BOLUS_COLOR (→ --iob-ring-first/second) for
// the insulin headers, var(--accent) for the Carbs header; no hardcoded hex.
// Header tints are translucent color-mix BACKGROUND fills (foreground stays
// fully opaque). The dose value uses the strongest primary text token
// (var(--ink)), never a hardcoded white. Labels are contrast-clamped toward
// --ink so a dark stock ring color (hunter green) still reads on the card.
//
// Inference is NOT reimplemented here: meal/correction classification and
// the carbs context come from `mealForContext` (the `blocks` prop). Graceful
// per-segment degradation throughout (no ~undefinedg / NaNu / dangling ·);
// fmtTime honors display_24h.

import { FoodGlyph } from "./FoodGlyph";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import type { FoodIconKey } from "../../lib/foodEmoji";
import {
  BASAL_RING_COLOR,
  CORRECTION_BOLUS_COLOR,
  formatBasalHoursLeft,
  MEAL_BOLUS_COLOR,
  NO_CARBS_LABEL,
  ringLabelInk,
  type BasalContext,
  type MealContextBlock,
} from "../../lib/iob";
import { fmtTime } from "../../lib/glucose";
import styles from "./MealContextLines.module.css";

interface Props {
  blocks: MealContextBlock[];
  h24: boolean;
  /** mobile → vertical section stack; kiosk/web → compact single-line rows. */
  variant: FrameVariant;
  /** Cycle 147: the long-acting dose on board — renders the violet
   *  Long-acting section (amber + the "Log {product}" nudge when due,
   *  relocated off the retired BasalLine meter). */
  basal?: BasalContext | null;
  /** Deep-link to the guided Basal Log path (/log?tab=insulin&basal=1);
   *  wired by NowScreen, no-op in the Settings preview. */
  onLogBasal?: () => void;
}

function doseText(units: number): string {
  if (Number.isNaN(units)) return "";
  return `${units % 1 === 0 ? units.toFixed(0) : units.toFixed(1)}u`;
}

// Header tint = a translucent background fill from the section's color
// (ring color or --accent). color-mix keeps it tokenized; the foreground
// (label / dot / value) stays fully opaque (kiosk legibility rule).
function tintBg(color: string): string {
  return `color-mix(in srgb, ${color} 15%, transparent)`;
}
// Cycle 343: the local 70/30 label-ink clamp is retired — it left the
// stock correction yellow (#ffd60a) illegible on a light/photo card
// (measured ~2.7:1). Header/section labels now use the shared, fixed
// `ringLabelInk` from lib/iob.ts (see its doc comment) so this file and
// BolusRings' ring-center countdowns can never disagree on the formula.

interface InsulinRow {
  correction: boolean;
  units: number;
  takenAt: number;
}
interface CarbsCtx {
  description: string;
  carbsG: number | null;
  foodIconKey: FoodIconKey;
  takenAt: number | null;
}

export function MealContextLines({
  blocks,
  h24,
  variant,
  basal = null,
  onLogBasal,
}: Props) {
  // Cycle 121: empty → render nothing (the Now screen hides the whole card
  // when there's nothing to show). Cycle 147: a basal on board keeps the
  // block alive so its Long-acting line renders even with no rapid doses.
  if (blocks.length === 0 && basal == null) return null;

  // Insulin rows: flatten the grouped blocks back to per-dose rows. A meal
  // block's doses + a forced-meal `bolus` block → "First bolus"; a
  // `correction` block → "Correction". Newest-first, capped at 2 (rings).
  const insulinRows: InsulinRow[] = [];
  for (const b of blocks) {
    if (b.kind === "correction") {
      insulinRows.push({ correction: true, units: b.units, takenAt: b.takenAt });
    } else if (b.kind === "bolus") {
      insulinRows.push({ correction: false, units: b.units, takenAt: b.takenAt });
    } else {
      for (const d of b.doses) {
        insulinRows.push({
          correction: false,
          units: d.units,
          takenAt: d.takenAt,
        });
      }
    }
  }
  insulinRows.sort((a, b) => b.takenAt - a.takenAt);
  const visibleInsulin = insulinRows.slice(0, 2);

  // Carbs context: the newest meal block carrying a food name and/or a carb
  // estimate (blocks are already newest-first). A lone first bolus with no
  // paired food has no meal block here → no carbs section.
  let carbs: CarbsCtx | null = null;
  for (const b of blocks) {
    if (b.kind === "meal" && (b.description !== "" || b.carbsG != null)) {
      const newestDose = b.doses.length
        ? Math.max(...b.doses.map((d) => d.takenAt))
        : null;
      carbs = {
        description: b.description,
        carbsG: b.carbsG,
        foodIconKey: b.foodIconKey,
        takenAt: newestDose,
      };
      break;
    }
  }

  const compact = variant !== "mobile";

  const insulinSection = (r: InsulinRow, i: number) => {
    const color = r.correction ? CORRECTION_BOLUS_COLOR : MEAL_BOLUS_COLOR;
    // Cycle 147: "First bolus" → "Bolus" (unified with the lone-bolus
    // path; the cycle-104c first-dose-is-meal classification unchanged).
    const label = r.correction ? "Correction" : "Bolus";
    const dose = doseText(r.units);
    const time = `at ${fmtTime(r.takenAt, { h24 })}`;
    const key = `i-${r.takenAt}-${i}`;
    if (compact) {
      return (
        <div className={styles.compactRow} key={key}>
          <span className={styles.headerPill} style={{ background: tintBg(color) }}>
            <span className={styles.dot} style={{ background: color }} />
            <span className={styles.label} style={{ color: ringLabelInk(color) }}>
              {label}
            </span>
          </span>
          {dose ? <span className={styles.value}>{dose}</span> : null}
          <span className={styles.timeMuted}>{time}</span>
        </div>
      );
    }
    return (
      <div className={styles.section} key={key}>
        <div className={styles.headerBand} style={{ background: tintBg(color) }}>
          <span className={styles.dot} style={{ background: color }} />
          <span className={styles.label} style={{ color: ringLabelInk(color) }}>
            {label}
          </span>
        </div>
        <div className={styles.valueLine}>
          {dose ? <span className={styles.value}>{dose}</span> : null}
          <span className={styles.timeMuted}>{time}</span>
        </div>
      </div>
    );
  };

  const accent = "var(--accent)";
  // Cycle 453.4: exactly 0 comes only from a bolus's own explicit 0 g with
  // nothing paired (`mealPairing`); it reads "No carbs", never "~0g".
  const carbsEst =
    carbs && carbs.carbsG === 0
      ? NO_CARBS_LABEL
      : carbs && carbs.carbsG != null && !Number.isNaN(carbs.carbsG)
        ? `~${Math.round(carbs.carbsG)}g`
        : null;
  const carbsTime =
    carbs && carbs.takenAt != null
      ? `at ${fmtTime(carbs.takenAt, { h24 })}`
      : null;

  const carbsSection = !carbs ? null : compact ? (
    <div className={styles.compactRow} key="carbs">
      <span className={styles.headerPill} style={{ background: tintBg(accent) }}>
        <span className={styles.glyph} aria-hidden>
          <FoodGlyph iconKey={carbs.foodIconKey} size={13} />
        </span>
        <span className={styles.label} style={{ color: accent }}>
          Carbs
        </span>
      </span>
      <span className={styles.carbsName}>{carbs.description || "Meal"}</span>
      {carbsEst ? <span className={styles.value}>{carbsEst}</span> : null}
    </div>
  ) : (
    <div className={styles.section} key="carbs">
      <div className={styles.headerBand} style={{ background: tintBg(accent) }}>
        <span className={styles.glyph} aria-hidden>
          <FoodGlyph iconKey={carbs.foodIconKey} size={13} />
        </span>
        <span className={styles.label} style={{ color: accent }}>
          Carbs
        </span>
      </div>
      <div className={styles.carbsBody}>
        <span className={styles.carbsName}>{carbs.description || "Meal"}</span>
        {carbsEst ? <span className={styles.carbsEst}>{carbsEst}</span> : null}
        {carbsTime ? <span className={styles.timeMuted}>{carbsTime}</span> : null}
      </div>
    </div>
  );

  // Cycle 147: the Long-acting section — violet (--iob-ring-third via
  // BASAL_RING_COLOR), amber + the "Log {product}" deep-link nudge when
  // due. Sourced from basalForContext (the `basal` prop); placed LAST
  // (after Correction and Carbs) consistently on every surface.
  const basalColor =
    basal != null && basal.due ? "var(--zone-warn)" : BASAL_RING_COLOR;
  const basalValue =
    basal != null
      ? `${basal.label ?? "Basal"} · ${doseText(basal.units)} · ${formatBasalHoursLeft(
          basal.hoursLeft,
          basal.due,
        )}`
      : "";
  const basalNudge =
    basal != null && basal.due && onLogBasal != null ? (
      <button
        type="button"
        className={styles.basalLogBtn}
        onClick={(e) => {
          e.stopPropagation();
          onLogBasal();
        }}
      >
        Log {basal.label ?? "basal"}
      </button>
    ) : null;
  const basalSection =
    basal == null ? null : compact ? (
      <div className={styles.compactRow} key="basal">
        <span
          className={styles.headerPill}
          style={{ background: tintBg(basalColor) }}
        >
          <span className={styles.dot} style={{ background: basalColor }} />
          <span className={styles.label} style={{ color: ringLabelInk(basalColor) }}>
            Long-acting
          </span>
        </span>
        <span className={`${styles.value} tnum`}>{basalValue}</span>
        {basalNudge}
      </div>
    ) : (
      <div className={styles.section} key="basal">
        <div
          className={styles.headerBand}
          style={{ background: tintBg(basalColor) }}
        >
          <span className={styles.dot} style={{ background: basalColor }} />
          <span className={styles.label} style={{ color: ringLabelInk(basalColor) }}>
            Long-acting
          </span>
        </div>
        <div className={styles.valueLine}>
          <span className={`${styles.value} tnum`}>{basalValue}</span>
          {basalNudge}
        </div>
      </div>
    );

  return (
    <div className={compact ? styles.rootCompact : styles.root}>
      {visibleInsulin.map(insulinSection)}
      {carbsSection}
      {basalSection}
    </div>
  );
}
